/* ✦ POLISH IT — the one Brill button for a piece she wrote or dictated
 * (her call, 2026-11-04). It replaced pressing Fix, Tighten and Search one
 * after another: "fix the spelling and grammar, tighten it a little bit
 * without rewriting, and make it search friendly … I still want to keep my
 * voice."
 *
 * HOW ONE POLISH RUNS, and why it is several calls, not one:
 *   1. Her piece is cut into parts at paragraph breaks (`polishParts`) and each
 *      part is polished in its own call, all at once. A whole long piece in one
 *      reply runs to 3,000+ tokens and close to the 60-second limit; parts come
 *      back in seconds. Each part is sent the whole piece as context, so a
 *      repeat across the piece can still be noticed.
 *   2. On the polished version, the existing Search friendly and Weave in my
 *      offer tasks run, if ticked: subheads and offer sentences go INTO the
 *      version, the title, the short answer, claims, FAQ and next step wait
 *      beside it for her to tick.
 * Nothing touches her draft until she presses Use this version.
 *
 * HOW FAR BRILL MAY GO is her choice of level, and the code holds it, not only
 * the prompt: on LIGHT, a part that comes back with more than a third of its
 * words changed is refused and kept as she wrote it, with a note saying so. On
 * DICTATION a part may be rebuilt, but one that keeps under a third of its words
 * is refused the same way, because that is Brill dropping her points.
 *
 * WHAT CHANGED is shown sentence by sentence (`polishDiff`), and each change can
 * be put back to her own words on its own (`revertHunk`).
 */

export const POLISH_LEVELS = [
  { id: "light", label: "Light", hint: "For a piece you wrote. Fixes, smooths a clumsy sentence, cuts a repeat. Most sentences come back untouched." },
  { id: "dictation", label: "Clean up my dictation", hint: "For a piece you spoke. Rebuilds sentences and order, keeps your phrases, stories and points." },
];
export const POLISH_DEFAULTS = { level: "light", tighten: true, search: true, offer: false };
export const POLISH_TOKENS = 4000;
const PART_WORDS = 350;
const MAX_PARTS = 6;
const LIGHT_MAX_CHANGE = 1 / 3;
const DICTATION_MIN_KEPT = 0.35;   // filler and false starts are often half of speech
const MAX_NOTES = 8;

const wordsOf = (s) => String(s || "").match(/\S+/g) || [];

/** Her piece cut at paragraph breaks into parts of about PART_WORDS, at most
 *  MAX_PARTS (a longer piece gets bigger parts). Joining the parts gives back
 *  her text exactly: every break stays with the part before it. */
export function polishParts(text) {
  const s = String(text || "");
  const paras = s.split(/(?<=\n[ \t]*\n+)/);
  const total = wordsOf(s).length;
  const size = Math.max(PART_WORDS, Math.ceil(total / MAX_PARTS));
  const parts = [];
  let cur = "", n = 0;
  for (const p of paras) {
    const w = wordsOf(p).length;
    if (cur && n + w > size && parts.length < MAX_PARTS - 1) { parts.push(cur); cur = ""; n = 0; }
    cur += p; n += w;
  }
  if (cur || !parts.length) parts.push(cur);
  return parts;
}

/** The [NEWSLETTER_WRITING] line for one part. */
export function buildPolishContext({ title = "", part, whole, opts = {} }) {
  const level = opts.level === "dictation" ? "DICTATION" : "LIGHT";
  const lines = [
    "[NEWSLETTER_WRITING — her own words; read only, do not reference as a conversation]",
    "Task: POLISH",
    `Level: ${level}`,
    `Tighten: ${opts.tighten ? "yes" : "no"}`,
    `Search friendly: ${opts.search ? "yes" : "no"}`,
    `Title: ${String(title).trim() || "(none yet)"}`,
  ];
  if (whole && whole !== part) {
    lines.push("", "The whole piece, for context only. Do NOT return it:", "<<<WHOLE_PIECE>>>", String(whole), "<<<WHOLE_PIECE_END>>>");
  }
  lines.push("", "The part to polish, exactly as she wrote it:", "<<<HER_PIECE>>>", String(part), "<<<HER_PIECE_END>>>");
  return lines.join("\n");
}

/** Brill's polished part, held to her level. Returns { text, notes, kept }:
 *  `kept` is true when the part was refused and her words stand. */
export function checkPolishedPart(original, data, level = "light") {
  const notes = (Array.isArray(data?.notes) ? data.notes : [])
    .map((n) => String(n || "").trim().slice(0, 240)).filter(Boolean).slice(0, 4);
  const raw = typeof data?.text === "string" ? data.text : "";
  const lead = String(original).match(/^\s*/)[0];
  const tail = String(original).match(/\s*$/)[0];
  const text = raw.trim() ? lead + herQuotes(original, raw.trim()) + tail : "";
  const was = wordsOf(original).length;
  if (!text) return { text: original, notes, kept: !!was };
  if (level === "dictation") {
    if (was && wordsOf(text).length < was * DICTATION_MIN_KEPT) {
      return { text: original, kept: true, notes: [...notes, `Brill cut most of the part starting “${firstWords(original)}”, so it is left as you said it.`] };
    }
    return { text, notes, kept: false };
  }
  if (wordChangeShare(original, text) > LIGHT_MAX_CHANGE) {
    return { text: original, kept: true, notes: [...notes, `Brill rewrote too much of the part starting “${firstWords(original)}”, so it is left as you wrote it.`] };
  }
  return { text, notes, kept: false };
}

const firstWords = (s) => wordsOf(s).slice(0, 6).join(" ");

/* HER APOSTROPHES. Brill hands back "don't" where she typed "don’t", and that
   alone marked nearly every sentence of her piece as changed on the first real
   try (2026-11-04). Her kind of apostrophe is put back wherever she used it. */
export function herQuotes(original, text) {
  const o = String(original);
  const curly = /\w’\w/.test(o), straight = /\w'\w/.test(o);
  if (curly && !straight) return String(text).replace(/(\w)'(\w)/g, "$1’$2");
  if (straight && !curly) return String(text).replace(/(\w)’(\w)/g, "$1'$2");
  return String(text);
}

/* ── Comparing her words with the version ─────────────────────────────────── */

/* Longest common subsequence of two token lists, as matched index pairs. */
function lcsPairs(a, b, same) {
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = same(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const pairs = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (same(a[i], b[j])) { pairs.push([i, j]); i += 1; j += 1; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) i += 1;
    else j += 1;
  }
  return pairs;
}

/** The share of her words that are not in the version, in order. */
export function wordChangeShare(a, b) {
  const x = wordsOf(a), y = wordsOf(b);
  if (!x.length) return y.length ? 1 : 0;
  const kept = lcsPairs(x, y, (p, q) => p === q).length;
  return Math.max(x.length - kept, y.length - kept) / x.length;
}

/** Text cut into sentences and line breaks, each carrying its own trailing
 *  space, so the pieces join back to the text exactly. */
export function sentencesOf(text) {
  const s = String(text || "");
  if (!s) return [];
  /* The space between sentences is its own piece, so putting a sentence back
     can never take a paragraph break with it or leave one behind. */
  return s.split(/((?<=[.!?…]["'”’)\]]*)[ \t]+(?=\S)|[ \t]*\n\s*)/).filter((t) => t !== "");
}
const isSpace = (s) => !String(s).trim();

/**
 * Her text against the version, sentence by sentence: a list of segments, each
 * { same: true, text } or { same: false, was, now }. Joining every segment's
 * `text` / `now` gives the version; `text` / `was` gives hers. A change that is
 * only spacing rides with the change beside it, or is marked `space` and never
 * counted or highlighted.
 */
export function polishDiff(original, version) {
  const a = sentencesOf(original), b = sentencesOf(version);
  const pairs = lcsPairs(a, b, (p, q) => p === q);
  const raw = [];
  let i = 0, j = 0;
  for (const [pi, pj] of [...pairs, [a.length, b.length]]) {
    const was = a.slice(i, pi).join(""), now = b.slice(j, pj).join("");
    if (was || now) raw.push({ same: false, was, now });
    if (pi < a.length) raw.push({ same: true, text: b[pj] });
    i = pi + 1; j = pj + 1;
  }
  /* Spacing between two changes in one paragraph, or a spacing-only change
     beside one, joins it. A paragraph break never does: changes in three
     paragraphs in a row read as one block she could only take or leave whole. */
  const out = [];
  for (let k = 0; k < raw.length; k += 1) {
    const seg = raw[k];
    const prev = out[out.length - 1];
    if (seg.same && isSpace(seg.text) && !seg.text.includes("\n") && prev && !prev.same && raw[k + 1] && !raw[k + 1].same) {
      prev.was += seg.text; prev.now += seg.text;
    } else if (!seg.same && prev && !prev.same) {
      prev.was += seg.was; prev.now += seg.now; prev.space = prev.space && isSpace(seg.was) && isSpace(seg.now);
    } else if (!seg.same) {
      out.push({ ...seg, space: isSpace(seg.was) && isSpace(seg.now) });
    } else {
      out.push(seg);
    }
  }
  return out;
}

/** The version with one change put back to her own words. */
export function revertHunk(original, version, index) {
  return polishDiff(original, version).map((seg, k) => (seg.same ? seg.text : k === index ? seg.was : seg.now)).join("");
}

/** The version with one change rewritten by HER: what she typed takes that
 *  change's place, keeping the spacing around it, so a paragraph break stays. */
export function editHunk(original, version, index, typed) {
  return polishDiff(original, version).map((seg, k) => {
    if (seg.same) return seg.text;
    if (k !== index) return seg.now;
    const around = seg.now.trim() ? seg.now : seg.was;
    const lead = around.match(/^\s*/)[0], tail = around.match(/\s*$/)[0];
    const words_ = String(typed || "").trim();
    return words_ ? lead + words_ + tail : "";
  }).join("");
}

export const changeCount =(original, version) => polishDiff(original, version).filter((s) => !s.same && !s.space).length;

/** Notes from every part, without repeats. */
export function mergeNotes(lists) {
  const seen = new Set();
  const out = [];
  for (const n of lists.flat()) {
    const k = n.toLowerCase().replace(/\W+/g, " ").trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(n);
  }
  return out.slice(0, MAX_NOTES);
}

/* ── Remembering her ticks (a convenience in this browser, nothing more) ──── */
const OPTS_KEY = "brill.polishOpts";
export function loadPolishOpts() {
  try {
    const v = JSON.parse(window.localStorage.getItem(OPTS_KEY) || "null");
    return v && typeof v === "object" ? { ...POLISH_DEFAULTS, ...v } : { ...POLISH_DEFAULTS };
  } catch {
    return { ...POLISH_DEFAULTS };
  }
}
export function savePolishOpts(opts) {
  try { window.localStorage.setItem(OPTS_KEY, JSON.stringify(opts)); } catch { /* private window: the ticks are just not remembered */ }
}
